/**
 * useVerticalSwipe Hook for LOOPINO AI Mobile
 * Hook for vertical swipe gestures (up/down) based on useMobileSwipe
 */

import { useState, useRef, useCallback, useEffect } from 'react';

export const useVerticalSwipe = (onSwipeUp, onSwipeDown, options = {}) => {
  const {
    threshold = 50,
    velocityThreshold = 0.3,
    touchStartDelay = 0,
    preventDefaultTouchmove = true,
    resistanceStiffness = 0.3,
    disabled = false,
    onSwipeStart,
    onSwipeEnd,
    onSwipeUpdate,
    returnAnimationDuration = 250,
    dampingFactor = 0.8
  } = options;

  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [startY, setStartY] = useState(0);
  const [currentX, setCurrentX] = useState(0);
  const [currentY, setCurrentY] = useState(0);
  const [startTime, setStartTime] = useState(0);
  const [isVerticalSwipe, setIsVerticalSwipe] = useState(false);
  const [swipeVelocity, setSwipeVelocity] = useState(0);

  const touchStartTimer = useRef(null);
  const animationFrameRef = useRef(null);
  const lastUpdateTime = useRef(0);
  const velocityHistory = useRef([]);

  // Calculate velocity with smoothing
  const calculateVelocity = useCallback((currentTime, currentPosition, startPosition, startTime) => {
    const timeDelta = currentTime - startTime;
    const distance = currentPosition - startPosition;
    
    if (timeDelta === 0) return 0;
    
    const instantVelocity = Math.abs(distance) / timeDelta;
    
    // Add to velocity history for smoothing
    velocityHistory.current.push(instantVelocity);
    if (velocityHistory.current.length > 5) {
      velocityHistory.current.shift();
    }
    
    // Return average velocity for smoother calculation
    return velocityHistory.current.reduce((a, b) => a + b, 0) / velocityHistory.current.length;
  }, []);

  // Apply resistance when swiping beyond boundaries
  const getResistantOffset = useCallback((offset, maxOffset = 300) => {
    if (Math.abs(offset) <= maxOffset * 0.7) return offset;
    
    const sign = offset < 0 ? -1 : 1;
    const normalizedOffset = Math.abs(offset) / maxOffset;
    
    // Use a more natural resistance curve
    const resistance = Math.pow(normalizedOffset, 1.5) * resistanceStiffness;
    const resistantOffset = maxOffset * 0.7 + (Math.abs(offset) - maxOffset * 0.7) * (1 - resistance);
    
    return sign * resistantOffset;
  }, [resistanceStiffness]);

  // Real-time swipe state update
  const updateSwipeState = useCallback(() => {
    if (!isDragging) return;
    
    const swipeState = {
      isDragging,
      swipeOffset: getResistantOffset(currentY - startY),
      rawSwipeOffset: currentY - startY,
      isVerticalSwipe,
      swipeDirection: (currentY - startY) > 0 ? 'down' : 'up',
      swipeProgress: Math.abs(currentY - startY) / threshold,
      velocity: swipeVelocity
    };
    
    if (onSwipeUpdate) {
      onSwipeUpdate(swipeState);
    }
  }, [isDragging, currentY, startY, isVerticalSwipe, threshold, swipeVelocity, onSwipeUpdate, getResistantOffset]);

  // Update swipe state on every frame while dragging
  useEffect(() => {
    if (isDragging) {
      const animate = () => {
        updateSwipeState();
        animationFrameRef.current = requestAnimationFrame(animate);
      };
      animationFrameRef.current = requestAnimationFrame(animate);
    } else {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    }
    
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isDragging, updateSwipeState]);

  const handleTouchStart = useCallback((e) => {
    if (disabled) return;
    
    const touch = e.touches[0];
    const currentTime = Date.now();
    
    setStartX(touch.clientX);
    setStartY(touch.clientY);
    setCurrentX(touch.clientX);
    setCurrentY(touch.clientY);
    setStartTime(currentTime);
    setIsVerticalSwipe(false);
    setSwipeVelocity(0);
    
    // Reset velocity history
    velocityHistory.current = [];
    lastUpdateTime.current = currentTime;
    
    // Call onSwipeStart callback
    if (onSwipeStart) {
      onSwipeStart();
    }
    
    if (touchStartDelay > 0) {
      touchStartTimer.current = setTimeout(() => {
        setIsDragging(true);
      }, touchStartDelay);
    } else {
      setIsDragging(true);
    }
  }, [touchStartDelay, disabled, onSwipeStart]);

  const handleTouchMove = useCallback((e) => {
    if (disabled || (!isDragging && touchStartDelay > 0)) return;
    
    const touch = e.touches[0];
    const currentTime = Date.now();
    const deltaX = touch.clientX - startX;
    const deltaY = touch.clientY - startY;
    
    // Determine if this is a vertical swipe
    if (!isVerticalSwipe && (Math.abs(deltaX) > 10 || Math.abs(deltaY) > 10)) {
      const vertical = Math.abs(deltaY) > Math.abs(deltaX);
      setIsVerticalSwipe(vertical);
      
      // Only prevent default for vertical swipes and only if the event is cancelable
      if (vertical && preventDefaultTouchmove && e.cancelable) {
        e.preventDefault();
      }
    }
    
    if (isVerticalSwipe || Math.abs(deltaY) > Math.abs(deltaX)) {
      setCurrentX(touch.clientX);
      setCurrentY(touch.clientY);
      
      // Calculate and update velocity with smoothing
      if (currentTime - lastUpdateTime.current > 16) { // ~60fps throttling
        const velocity = calculateVelocity(currentTime, touch.clientY, startY, startTime);
        setSwipeVelocity(velocity);
        lastUpdateTime.current = currentTime;
      }
      
      // Only prevent default if the event is cancelable
      if (preventDefaultTouchmove && e.cancelable) {
        e.preventDefault();
      }
    }
  }, [isDragging, startX, startY, isVerticalSwipe, preventDefaultTouchmove, touchStartDelay, disabled, calculateVelocity, startTime]);

  const handleTouchEnd = useCallback(() => {
    if (touchStartTimer.current) {
      clearTimeout(touchStartTimer.current);
    }
    
    if (!isDragging || disabled) {
      // Call onSwipeEnd even if disabled to clean up state
      if (onSwipeEnd) {
        onSwipeEnd();
      }
      return;
    }
    
    const deltaX = currentX - startX;
    const deltaY = currentY - startY;
    const deltaTime = Date.now() - startTime;
    const finalVelocity = swipeVelocity * dampingFactor; // Apply damping
    
    // Only process vertical swipes
    if (Math.abs(deltaY) > Math.abs(deltaX)) {
      // Enhanced threshold logic with velocity consideration
      const effectiveThreshold = Math.max(threshold * 0.5, threshold - (finalVelocity * 100));
      
      if (Math.abs(deltaY) > effectiveThreshold || finalVelocity > velocityThreshold) {
        if (deltaY > 0) {
          // Swipe down
          onSwipeDown?.(Math.abs(deltaY), finalVelocity);
        } else {
          // Swipe up
          onSwipeUp?.(Math.abs(deltaY), finalVelocity);
        }
      }
    }
    
    // Reset state
    setIsDragging(false);
    setStartX(0);
    setStartY(0);
    setCurrentX(0);
    setCurrentY(0);
    setStartTime(0);
    setIsVerticalSwipe(false);
    setSwipeVelocity(0);
    velocityHistory.current = [];
    
    // Call onSwipeEnd callback
    if (onSwipeEnd) {
      onSwipeEnd();
    }
  }, [isDragging, currentX, startX, currentY, startY, startTime, threshold, velocityThreshold, onSwipeUp, onSwipeDown, disabled, onSwipeEnd, swipeVelocity, dampingFactor]);

  const swipeOffset = isDragging ? currentY - startY : 0;

  // Reset function with smooth return animation
  const reset = useCallback(() => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
    
    setIsDragging(false);
    setStartX(0);
    setStartY(0);
    setCurrentX(0);
    setCurrentY(0);
    setIsVerticalSwipe(false);
    setSwipeVelocity(0);
    velocityHistory.current = [];
    
    // Call onSwipeEnd on reset
    if (onSwipeEnd) {
      onSwipeEnd();
    }
  }, [onSwipeEnd]);

  return {
    // Touch event handlers
    onTouchStart: handleTouchStart,
    onTouchMove: handleTouchMove,
    onTouchEnd: handleTouchEnd,
    
    // Swipe state
    isDragging,
    swipeOffset: getResistantOffset(swipeOffset),
    rawSwipeOffset: swipeOffset,
    isVerticalSwipe,
    
    // Enhanced gesture properties
    swipeDirection: swipeOffset > 0 ? 'down' : 'up',
    swipeProgress: Math.abs(swipeOffset) / threshold,
    velocity: swipeVelocity,
    
    // Helper methods
    reset,
    
    // Advanced state information
    isResistant: Math.abs(swipeOffset) > 200,
    resistanceLevel: Math.min(1, Math.abs(swipeOffset) / 300),
    shouldTrigger: Math.abs(swipeOffset) > threshold || swipeVelocity > velocityThreshold
  };
};